/**
 * Matriz pública de tarifas (ES/EN). Espejo de la versión publicada de
 * plans/catalog_items/plan_items: si cambias la matriz en el back office,
 * actualiza también este archivo para la web pública.
 */

export type MatrixCell =
  | { kind: "yes" }
  | { kind: "no" }
  | { kind: "limit"; value: number }
  | { kind: "limited" }
  | { kind: "extra" }
  | { kind: "unlimited" };

export interface MatrixRow {
  label: { es: string; en: string };
  sublabel?: { es: string; en: string };
  cells: [MatrixCell, MatrixCell, MatrixCell, MatrixCell]; // lite, basico, club_pro, completo
}

export interface MatrixSection {
  title: { es: string; en: string };
  tag?: { es: string; en: string };
  rows: MatrixRow[];
}

export type PricingPlanKey = "lite" | "basico" | "club_pro" | "completo";
export type PricingCardContext = "home" | "comparison";

const HOME_CTA = { es: "Seleccionar", en: "Select" } as const;
const HOME_LITE_CTA = { es: "Empieza gratis", en: "Start free" } as const;
export function getPricingCardCopy(planKey: PricingPlanKey, _context: PricingCardContext, lang: "es" | "en") {
  return {
    cta: planKey === "lite" ? HOME_LITE_CTA[lang] : HOME_CTA[lang],
    note: null,
  };
}

/** Frase única bajo las 4 fichas de la home. */
export const PRICING_HOME_FOOTER = {
  es: "Prueba 7 días gratis · Sin permanencia · Cancela cuando quieras",
  en: "7-day free trial · No commitment · Cancel anytime",
} as const;

const Y: MatrixCell = { kind: "yes" };
const N: MatrixCell = { kind: "no" };
const E: MatrixCell = { kind: "extra" };
const U: MatrixCell = { kind: "unlimited" };
const ONE: MatrixCell = { kind: "limit", value: 1 };

/** Cupos mensuales del Agente Retail IA: única fuente de estas cifras. */
export const AGENT_QUOTAS = { lite: 3, basico: 30, club_pro: 60, completo: 100 } as const;

export const PRICING_PLANS = [
  {
    key: "lite",
    highlights: [{ es: "3 consultas al Agente Retail IA", en: "3 Retail AI Agent queries" }, { es: "1 uso de la calculadora de viabilidad", en: "1 viability calculator use" }, { es: "1 diagnóstico de locales", en: "1 premises diagnosis" }, { es: "1 detector exprés", en: "1 express detector" }],
    name: "Lite",
    price: { es: "0 €", en: "€0" },
    period: { es: "", en: "" },
    tagline: { es: "Para empezar a explorar", en: "To start exploring" },
    cta: { es: "Empieza gratis", en: "Start for free" },
  },
  {
    key: "basico",
    highlights: [{ es: "Todo lo de Lite", en: "Everything in Lite" }, { es: "30 consultas al Agente Retail IA", en: "30 Retail AI Agent queries" }, { es: "Calculadora, diagnóstico y detector ilimitados", en: "Unlimited calculator, diagnosis and detector" }, { es: "Guías empresariales", en: "Business guides" }, { es: "Listado de subvenciones", en: "Grants directory" }, { es: "Música libre de derechos, limitada", en: "Royalty-free music, limited" }],
    name: "Básico",
    nameEn: "Basic",
    price: { es: "39 €", en: "€39" },
    period: { es: "/mes", en: "/mo" },
    tagline: { es: "Para preparar tu apertura", en: "To prepare your opening" },
    cta: HOME_CTA,
  },
  {
    key: "club_pro",
    highlights: [{ es: "Todo lo de Básico", en: "Everything in Basic" }, { es: "60 consultas al Agente Retail IA", en: "60 Retail AI Agent queries" }, { es: "Hoja de ruta y KPIs", en: "Roadmap and KPIs" }, { es: "4 generaciones de anuncios con IA", en: "4 AI ad generations" }, { es: "Música comercial · Extra", en: "Commercial music · Extra", extra: true }],
    name: "Club Pro",
    price: { es: "69 €", en: "€69" },
    period: { es: "/mes", en: "/mo" },
    tagline: { es: "Para controlar tu negocio", en: "To run your business" },
    cta: HOME_CTA,
    featured: true,
  },
  {
    key: "completo",
    highlights: [{ es: "Todo lo de Club Pro", en: "Everything in Club Pro" }, { es: "100 consultas al Agente Retail IA", en: "100 Retail AI Agent queries" }, { es: "12 generaciones de anuncios con IA", en: "12 AI ad generations" }, { es: "Mentores · Extra", en: "Mentors · Extra", extra: true }, { es: "Acceso a proveedores · Extra", en: "Supplier access · Extra", extra: true }, { es: "Consulta con abogados · Extra", en: "Lawyer consultations · Extra", extra: true }, { es: "Tramitación de licencias · Extra", en: "Licence processing · Extra", extra: true }],
    name: "Avanzado",
    nameEn: "Advanced",
    price: { es: "99 €", en: "€99" },
    period: { es: "/mes", en: "/mo" },
    tagline: { es: "Para impulsar tu crecimiento", en: "To drive your growth" },
    cta: HOME_CTA,
  },
] as const;

export const PRICING_SECTIONS: MatrixSection[] = [
  {
    title: { es: "Fase inicial", en: "Getting started" },
    rows: [
      { label: { es: "Detector exprés", en: "Express detector" }, cells: [ONE, Y, Y, Y] },
      { label: { es: "Calculadora de viabilidad", en: "Viability calculator" }, cells: [ONE, Y, Y, Y] },
      { label: { es: "Diagnóstico de locales", en: "Premises diagnosis" }, cells: [ONE, Y, Y, Y] },
      { label: { es: "Agente Retail IA", en: "Retail AI Agent" }, sublabel: { es: "consultas al mes", en: "queries per month" }, cells: [{ kind: "limit", value: AGENT_QUOTAS.lite }, { kind: "limit", value: AGENT_QUOTAS.basico }, { kind: "limit", value: AGENT_QUOTAS.club_pro }, { kind: "limit", value: AGENT_QUOTAS.completo }] },
      { label: { es: "Guías empresariales", en: "Business guides" }, cells: [N, Y, Y, Y] },
      { label: { es: "Listado de subvenciones", en: "Grants directory" }, cells: [N, Y, Y, Y] },
      { label: { es: "Música libre de derechos", en: "Royalty-free music" }, sublabel: { es: "Escoge entre múltiples opciones", en: "Choose from multiple options" }, cells: [N, { kind: "limited" }, Y, Y] },
    ],
  },
  {
    title: { es: "Control de tu negocio", en: "Business control" },
    rows: [
      { label: { es: "Hoja de ruta", en: "Roadmap" }, cells: [N, N, Y, Y] },
      { label: { es: "KPIs", en: "KPIs" }, cells: [N, N, Y, Y] },
      { label: { es: "Detector completo", en: "Full detector" }, cells: [N, N, Y, Y] },
      { label: { es: "Subvenciones que encajan contigo", en: "Grants that fit you" }, cells: [N, N, Y, Y] },
      { label: { es: "Casos y soluciones", en: "Cases and solutions" }, cells: [N, N, Y, Y] },
      { label: { es: "Generaciones de anuncios con IA", en: "AI ad generations" }, cells: [N, N, { kind: "limit", value: 4 }, { kind: "limit", value: 12 }] },
    ],
  },
  {
    title: { es: "Expansión y crecimiento", en: "Expansion and growth" },
    tag: { es: "premium", en: "premium" },
    rows: [
      { label: { es: "Alta en Google Business", en: "Google Business setup" }, cells: [N, N, N, Y] },
      { label: { es: "Módulos premium", en: "Premium modules" }, sublabel: { es: "Digitalización y tecnología · Legal, fiscal y administrativo", en: "Digital & Tech · Legal, Tax & Admin" }, cells: [N, N, N, Y] },
    ],
  },
  {
    title: { es: "Extras", en: "Extras" },
    tag: { es: "tarifa propia", en: "own pricing" },
    rows: [
      { label: { es: "Publicaciones en otras redes", en: "Posts on other networks" }, cells: [N, N, E, E] },
      { label: { es: "Música comercial", en: "Commercial music" }, sublabel: { es: "Puede requerir licencia de SGAE o la entidad equivalente de tu país", en: "May require a licence from your country's collecting society" }, cells: [N, N, E, E] },
      { label: { es: "Aroma para tu local", en: "Scent for your store" }, cells: [N, N, N, E] },
      { label: { es: "Alarma para tu local", en: "Alarm for your store" }, cells: [N, N, N, E] },
      { label: { es: "Consulta con abogados", en: "Lawyer consultations" }, cells: [N, N, N, E] },
      { label: { es: "Tramitación de licencias", en: "Licence processing" }, cells: [N, N, N, E] },
      { label: { es: "Acceso a mentores", en: "Access to mentors" }, cells: [N, N, N, E] },
      { label: { es: "Acceso a proveedores", en: "Supplier access" }, cells: [N, N, N, E] },
      { label: { es: "Servicio de SEO", en: "SEO service" }, cells: [N, N, N, E] },
      { label: { es: "Preparar subvención", en: "Grant preparation" }, sublabel: { es: "Preparamos la memoria y los papeles", en: "We prepare the report and paperwork" }, cells: [N, E, E, E] },
    ],
  },
];

export const PRICING_FOOTNOTES = [
  {
    es: "Consultas: preguntas al Agente Retail IA Lemon cada mes.",
    en: "Queries: questions to the Retail Lemon Retail AI Agent each month.",
  },
  {
    es: "Música libre de derechos: escoge entre múltiples opciones. Música comercial: para ambientar tu local; puede requerir licencia de SGAE o la entidad equivalente de tu país y se contrata aparte.",
    en: "Royalty-free music: choose from multiple options. Commercial music: for your store ambience; it may require a licence from your country's collecting society and is purchased separately.",
  },
  {
    es: "Extra: módulo opcional con tarifa propia. Mentores y proveedores tienen tarifa propia.",
    en: "Extra: optional module with its own pricing. Mentors and providers have their own rates.",
  },
];

export const PRICING_NO_COMMITMENT = {
  es: "Sin permanencia · Cancela cuando quieras",
  en: "No commitment · Cancel anytime",
};
